import 'custom-event-polyfill';
import { BroadcastChannel } from 'broadcast-channel';

import { vrmConfig } from '@/config';

// Detect current version with __version
if (!Number.isFinite(window.__version)) {
    window.__version = 0;
    console.error(
        'The version script has not loaded, setting version to 0. Update likely to be found when user is (back) online',
    );
}

export const currentVersion = window.__version;
const CURRENT_VERSION_MESSAGE = 'current-version';

const UPDATE_CHECK_INTERVAL = 3 * 60 * 1000; // 3 minutes
const RELOAD_AFTER_TIME = 4 * 60 * 1000; // 4 minutes
export const RELOAD_PAGE_EVENT = 'vrmAppUpdateAvailable';
var lastTimer = null;
var lastMouseX = null;
var lastMouseY = null;
var pageLeftTs = null;
var pageReturnedTs = null;
var currentlyVisible = true; // true by default when loaded

const setupChannel = () => {
    const versionBroadcastChannel = new BroadcastChannel('latest-version', {
        idb: {
            onclose: () => {
                versionBroadcastChannel.close();
                setupChannel();
            },
        },
    });

    const broadcastMyVersion = () =>
        versionBroadcastChannel.postMessage({
            type: CURRENT_VERSION_MESSAGE,
            version: currentVersion,
        });
    // broadcast version on first load
    broadcastMyVersion();

    // listen for other tabs versions
    versionBroadcastChannel.onmessage = event => {
        if (event && event.type === CURRENT_VERSION_MESSAGE) {
            if (currentVersion !== event.version) {
                // some other tab has a newer version
                performUpdateCheck();
            }
        }
    };
};
setupChannel();

import axios from 'axios';
const endpoint = axios.create({
    baseURL: window.location.origin,
});

const logger = console.info.bind(console, '[update-detector]');

export function disableUpdateChecks() {
    window.__skipUpdateChecks = true;
}

export function enableUpdateChecks() {
    window.__skipUpdateChecks = false;
}
/**
 * Detect if there is an update available of the app.
 * If the server has a newer version of the app this resolves to true.
 * Rejects in case of network and server errors
 * @returns {Promise<boolean>}
 */
async function checkUpdateAvailable() {
    const { data: /** @type {string} */ versionScript } = await endpoint.get(
        `/version.js?cachebust=${new Date().getTime()}`,
        {
            responseType: 'application/javascript',
        },
    );
    const serverVersion = parseInt(versionScript.match(/\d+/));
    logger('Current version', typeof serverVersion, serverVersion);
    logger('Checking for update received server version', typeof currentVersion, currentVersion);

    console.assert(Number.isInteger(serverVersion), 'serverVersion is not an integer');
    return currentVersion !== serverVersion && serverVersion;
}

export function clearTimer() {
    if (lastTimer) {
        clearTimeout(lastTimer);
    }
    lastTimer = null;
}

async function checkUpdate() {
    //Manual refresh case
    const updateAvailable = await checkUpdateAvailable();
    if (!updateAvailable) {
        sessionStorage.updateAvailable = 0;
        //clear whatever timer was going on
        clearTimer();
    }
}

checkUpdate();

async function performUpdateCheck() {
    logger('Performing update check');
    let serverVersion = false;
    // In some cases we want to skip update checks (e.g. when updating firmware)
    // If that global flag is set, skip till the next check
    if (window.__skipUpdateChecks) {
        logger('Skipping check, disable flag set');
        scheduleUpdateCheck();
        return;
    }
    try {
        serverVersion = await checkUpdateAvailable();
        if (serverVersion) {
            logger('App update available');
            //get timestamp of when we got the new version
            sessionStorage.updateAvailable = 1;
            startTimer();
        } else {
            sessionStorage.updateAvailable = 0;
        }
    } finally {
        if (!serverVersion) {
            scheduleUpdateCheck();
        }
    }
}

export function updateLastActive(e) {
    if (e.type == 'mousemove') {
        if (e.pageX == lastMouseX && e.pageY == lastMouseY) {
            startTimer();
            return;
        }
        lastMouseX = e.pageX;
        lastMouseY = e.pageY;
    } else {
        startTimer();
        return;
    }
}

function startTimer() {
    if (sessionStorage.updateAvailable === '1' && currentlyVisible) {
        clearTimer();
        lastTimer = setTimeout(reloadPage, RELOAD_AFTER_TIME);
    } else {
        clearTimer();
    }
}

function reloadPage() {
    window.dispatchEvent(new CustomEvent(RELOAD_PAGE_EVENT));
}
function scheduleUpdateCheck() {
    setTimeout(performUpdateCheck, UPDATE_CHECK_INTERVAL);
}

['keydown', 'keyup', 'scroll', 'mousemove', 'mousedown', 'click', 'pointerdown'].forEach(evt => {
    window.document.addEventListener(evt, updateLastActive);
});

window.addEventListener('page-visible', e => {
    if (e.detail.isVisible) {
        pageReturnedTs = new Date();
        currentlyVisible = true;
        //This means user has left the page and just came back
        if (pageReturnedTs - pageLeftTs > RELOAD_AFTER_TIME) {
            //check that an update is available
            window.dispatchEvent(new CustomEvent(RELOAD_PAGE_EVENT));
        } else {
            //user has come back to the page before and stays in top of navbar without moving the mouse nor clicking
            startTimer();
        }
    } else if (!e.detail.isVisible) {
        // kill the timers as they will be throttled on different browsers -> unexpected behaviour
        clearTimer();
        pageLeftTs = new Date();
        currentlyVisible = false;
        //stop the timer
        //store time left until timer happens
    }
});

window.addEventListener(RELOAD_PAGE_EVENT, () => {
    //when changing page
    if (sessionStorage.updateAvailable === '1') {
        //clear timer
        clearTimer();
        sessionStorage.updateAvailable = 0;
        //page has been updated flag to display the banner
        sessionStorage.updatedPage = 1;
        setTimeout(function() {
            window.location.reload();
        }, 100);
    }
});

if (!vrmConfig.debug) {
    logger('Activating update checker, current app version is', currentVersion);
    scheduleUpdateCheck();
} else {
    logger('Auto reload disabled in debug mode');
}
